import { useState, useEffect } from 'react';
import { Sparkles, CalendarClock, Save, Clock, Image as ImageIcon, X, Lightbulb } from 'lucide-react';
import api from '../services/apiClient';
import useAuthStore from '../store/authStore';
import { Button, Field, Input, Textarea, AutoTextarea } from '../components/ui';
import { toast } from '../components/toast';
import ImageModal from '../components/ImageModal';
import Lightbox from '../components/Lightbox';
import MultiPost from '../components/generate/MultiPost';
import { NETWORKS, NETWORK_LABELS, NETWORK_ICONS, NETWORK_COLORS } from '../lib/networks';

export default function Generate() {
  const currentCompany = useAuthStore((s) => s.currentCompany);
  const [brief, setBrief] = useState('');
  const [tone, setTone] = useState('');
  const [selected, setSelected] = useState(['instagram', 'facebook', 'linkedin']);
  const [generating, setGenerating] = useState(false);
  const [post, setPost] = useState(null);
  const [targets, setTargets] = useState([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [bestTimes, setBestTimes] = useState(null);
  const [mode, setMode] = useState('single'); // 'single' | 'multi'
  const [images, setImages] = useState({}); // targetId -> url
  const [imageModalFor, setImageModalFor] = useState(null); // targetId aberto no modal
  const [lightbox, setLightbox] = useState(null); // url ampliada

  const companyId = currentCompany?.id;

  const onImageChange = (targetId, mediaArr) => {
    setImages((m) => ({ ...m, [targetId]: mediaArr || [] }));
  };

  useEffect(() => {
    if (!companyId) return;
    api.get('/analytics/best-times').then(setBestTimes).catch(() => {});
  }, [companyId]);

  // Proxima ocorrencia de (weekday, hour) como string para datetime-local
  const nextSlot = (weekday, hour) => {
    const d = new Date();
    d.setSeconds(0, 0);
    d.setHours(hour, 0);
    let diff = (weekday - d.getDay() + 7) % 7;
    if (diff === 0 && d.getTime() < Date.now()) diff = 7;
    d.setDate(d.getDate() + diff);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Sugestoes da 1a rede selecionada
  const suggestions = bestTimes && selected[0] ? (bestTimes.byNetwork[selected[0]] || []) : [];

  const toggle = (n) => setSelected((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));

  const generate = async () => {
    if (!brief.trim()) return toast.error('Escreva um brief');
    if (selected.length === 0) return toast.error('Selecione ao menos uma rede');
    setGenerating(true);
    try {
      const data = await api.post('/ai/generate', { brief, tone, networks: selected });
      setPost(data.post);
      setTargets(data.targets);
      toast.success('Conteudo gerado!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const setTargetText = (id, text) => setTargets((ts) => ts.map((t) => (t.id === id ? { ...t, text } : t)));

  const saveEdits = async () => {
    await api.put(`/posts/${post.id}`, { targets: targets.map((t) => ({ id: t.id, text: t.text })) });
  };

  const approve = async () => {
    setBusy(true);
    try {
      await saveEdits();
      await api.post(`/posts/${post.id}/approve`);
      toast.success('Aprovado — disponivel no Calendario');
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const schedule = async () => {
    if (!scheduledAt) return toast.error('Escolha data e hora');
    setBusy(true);
    try {
      await saveEdits();
      const iso = new Date(scheduledAt).toISOString();
      const res = await api.post(`/posts/${post.id}/schedule`, { scheduledAt: iso });
      const fails = (res.results || []).filter((r) => !r.ok);
      if (fails.length) toast.error(`Agendado com falhas em: ${fails.map((f) => f.network).join(', ')}`);
      else toast.success('Agendado no Metricool!');
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (!companyId) return <p className="muted">Selecione uma empresa no topo.</p>;

  return (
    <div>
      <div className="cal-tabs mb16">
        <button className={`cal-tab ${mode === 'single' ? 'on' : ''}`} onClick={() => setMode('single')}>Um post</button>
        <button className={`cal-tab ${mode === 'multi' ? 'on' : ''}`} onClick={() => setMode('multi')}>Vários posts de uma vez</button>
      </div>

      {mode === 'multi' ? <MultiPost /> : (
      <>
      <div className="card" style={{ maxWidth: 820 }}>
        <Field label="Brief (sobre o que postar)">
          <Textarea rows={3} value={brief} onChange={(e) => setBrief(e.target.value)}
            placeholder="Ex: Promocao de inverno com 20% off em toda a linha, ate sexta." />
        </Field>
        <Field label="Tom (opcional)">
          <Input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Ex: divertido e direto" />
        </Field>
        <Field label="Redes">
          <div className="row wrap">
            {NETWORKS.map((n) => {
              const Icon = NETWORK_ICONS[n];
              const on = selected.includes(n);
              return (
                <span key={n} className={`net-chip ${on ? 'on' : ''}`} onClick={() => toggle(n)}>
                  <Icon size={15} color={on ? NETWORK_COLORS[n] : '#9aa3af'} /> {NETWORK_LABELS[n]}
                </span>
              );
            })}
          </div>
        </Field>
        <Button onClick={generate} loading={generating}><Sparkles size={16} /> Gerar com IA</Button>
      </div>

      {post && (
        <div className="card mt16" style={{ maxWidth: 820 }}>
          <h3 style={{ marginTop: 0 }}>Conteúdo gerado</h3>
          {targets.map((t) => {
            const Icon = NETWORK_ICONS[t.network];
            const media = images[t.id] || [];
            return (
              <div key={t.id} className="gen-card">
                <div className="gen-head">
                  <span className="net-name">
                    <Icon size={17} color={NETWORK_COLORS[t.network]} />
                    {NETWORK_LABELS[t.network]}
                  </span>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="muted" style={{ fontSize: 12 }}>{(t.text || '').length} caracteres</span>
                    <Button variant="secondary" className="sm" onClick={() => setImageModalFor(t.id)}>
                      <ImageIcon size={14} /> {media.length ? `Imagens (${media.length})` : 'Adicionar imagem'}
                    </Button>
                  </div>
                </div>
                <div className="gen-body">
                  <AutoTextarea value={t.text || ''} onChange={(e) => setTargetText(t.id, e.target.value)} />
                  {media.length > 0 && (
                    <div className="gen-thumbs">
                      {media.map((m, i) => (
                        <button key={m.key} type="button" className="gen-thumb" onClick={() => setLightbox({ images: media.map((x) => x.url), index: i })} title="Ver ampliada">
                          <img src={m.url} alt="Imagem do post" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {t.imageSuggestion && (
                  <div className="suggestion-box">
                    <div className="sb-head"><Lightbulb size={12} /> Sugestão de imagem/carrossel (não vai no post)</div>
                    {t.imageSuggestion}
                  </div>
                )}
              </div>
            );
          })}

          <div className="row wrap mt8" style={{ alignItems: 'flex-end' }}>
            <Field label="Agendar para">
              <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
              {suggestions.length > 0 && (
                <div className="row wrap mt8">
                  <span className="muted" style={{ fontSize: 12 }}><Clock size={12} /> Sugestoes:</span>
                  {suggestions.map((s, i) => (
                    <span key={i} className="net-chip" onClick={() => setScheduledAt(nextSlot(s.weekday, s.hour))}>
                      {s.label}{s.source === 'default' ? '' : ' ★'}
                    </span>
                  ))}
                </div>
              )}
            </Field>
            <Button variant="secondary" onClick={approve} loading={busy}><Save size={15} /> Salvar / Aprovar</Button>
            <Button onClick={schedule} loading={busy}><CalendarClock size={15} /> Agendar no Metricool</Button>
          </div>
          <p className="hint">A publicação é feita pelo Metricool na data agendada (autoPublish). Vincule a marca em Conexões antes.</p>
        </div>
      )}

      {imageModalFor && (() => {
        const t = targets.find((x) => x.id === imageModalFor);
        if (!t) return null;
        return (
          <ImageModal
            targetId={t.id}
            network={t.network}
            currentMedia={images[t.id] || []}
            onChange={(mediaArr) => onImageChange(t.id, mediaArr)}
            onClose={() => setImageModalFor(null)}
          />
        );
      })()}

      <Lightbox images={lightbox?.images} index={lightbox?.index || 0} onClose={() => setLightbox(null)} />
      </>
      )}
    </div>
  );
}
